import React, { useMemo, useEffect } from 'react';
import intl from 'react-intl-universal';
import { useSelector, useDispatch } from 'react-redux';
import {
  Typography,
  FormItem,
  TextField,
  TextArea,
  Avatar,
  UsersPicker,
  GlobalBanner,
  Tag,
} from '@getsynapse/design-system';
import { useFlags } from 'launchdarkly-react-client-sdk';
import get from 'lodash/get';
import { getUserInitials, getUserDisplayName } from 'utils/functions';
import { AvatarUser, User, UserAvatars, FormOption } from 'utils/customTypes';
import { ProjectTemplate } from 'utils/types/templates';
import {
  selectLDUsersForDropdown,
  getLDUsers,
} from 'state/UsersManagement/usersManagementSlice';
import {
  selectLearningTeamsForDropdown,
  getLearningTeams,
  selectLearningTeamIsFetched,
} from 'state/LearningTeams/learningTeamsSlice';
import MultiSelectDropdown from 'Organisms/MultiSelectDropdow/MultiSelectDropdown';
import cognotaAvatar from 'assets/images/cognota-avatar.svg';
import { DeletedUser } from 'Atoms/DeletedUser';

const TemplateOwner: React.FC<{
  system: boolean;
  owner_id?: string;
  owner?: User | null;
  ldUsers: UserAvatars[];
  onUpdateTemplateOwner: (userId: string) => void;
}> = ({ system, owner_id, ldUsers, onUpdateTemplateOwner, owner }) => {
  const currentOwner = useMemo(() => {
    if (!owner) return [];

    return ldUsers.filter((ldUser) => ldUser.value === owner_id);
  }, [ldUsers, owner_id, owner]);

  return system ? (
    <TemplateCreator system />
  ) : (
    <UsersPicker
      deactivatedUserText={intl.get('DEACTIVATED')}
      usersList={ldUsers}
      selectedUsersList={currentOwner}
      onChange={(users: UserAvatars[]) =>
        onUpdateTemplateOwner(get(users, '[0].value', null))
      }
      triggerProps={{ 'data-cy': 'template-owner-field' }}
      maxLimit={1}
    />
  );
};

const TemplateCreator: React.FC<{ creator?: User; system: boolean }> = ({
  creator = null,
  system = false,
}) => {
  const templateCreator: Partial<AvatarUser> = {};
  if (creator) {
    templateCreator.avatar_url = creator.avatar_url;
    templateCreator.data = {
      firstName: creator.data.firstName,
      lastName: creator.data.lastName,
    };
  }

  if (system) {
    templateCreator.avatar_url = cognotaAvatar;
    templateCreator.data = {
      firstName: intl.get('SETTINGS_PAGE.PROJECTS_PAGE.SYSTEM'),
      lastName: '',
    };
  }

  if (!creator && !system) {
    return <DeletedUser />;
  }

  return (
    <div className='flex items-start'>
      <Avatar
        iconSrc={templateCreator.avatar_url}
        iconProps={{ className: 'text-lg' }}
        initial={getUserInitials(creator as User)}
        bgColorClass='bg-secondary'
      />
      <div className='ml-2'>
        <Typography
          variant='body'
          className='text-primary font-semibold'
          data-cy='template-creator-field'
        >
          {getUserDisplayName(templateCreator as User)}
        </Typography>
      </div>
    </div>
  );
};

const Overview: React.FC<{
  templateData: ProjectTemplate;
  updateTemplateData: (key: keyof ProjectTemplate, value: any) => void;
  assignedTeamIds?: string[];
  onAssignedTeamsChange?: (ids: string[]) => void;
  isAssignedTeamsLoading?: boolean;
}> = ({
  templateData,
  updateTemplateData,
  assignedTeamIds,
  onAssignedTeamsChange,
  isAssignedTeamsLoading = false,
}) => {
  const { id, system, creator, owner, owner_id, description, name } =
    templateData;
  const dispatch = useDispatch();
  const ldUsers = useSelector(selectLDUsersForDropdown);
  const { teamScopingProjectTemplates = false } = useFlags();
  const learningTeamsForDropdown = useSelector(selectLearningTeamsForDropdown);
  const learningTeamsFetched = useSelector(selectLearningTeamIsFetched);
  const isUpdating = id ? true : false;
  const canUpdate = isUpdating ? !system : true;
  const displayBanner = isUpdating && !system;

  useEffect(() => {
    if (ldUsers && ldUsers.length === 0) {
      dispatch(getLDUsers());
    }
  }, [ldUsers, dispatch]);

  useEffect(() => {
    if (teamScopingProjectTemplates && !learningTeamsFetched) {
      dispatch(getLearningTeams());
    }
  }, [teamScopingProjectTemplates, learningTeamsFetched, dispatch]);

  const handleUpdateTemplateOwner = (userId: string) => {
    updateTemplateData('owner_id', userId);
  };

  const selectedTeamValues = useMemo(
    () =>
      learningTeamsForDropdown.filter((option) =>
        assignedTeamIds?.includes(option.value)
      ),
    [learningTeamsForDropdown, assignedTeamIds]
  );

  return (
    <div className='mt-4'>
      <Typography variant='body2' className='text-neutral-dark'>
        {intl.get(
          'SETTINGS_PAGE.TEMPLATE_PAGE.OVERVIEW_SECTION.SECTION_DESCRIPTION'
        )}
      </Typography>
      {displayBanner && (
        <GlobalBanner
          variant='warning'
          className='mt-2.5'
          data-cy='teamplate-overview__update-warning'
        >
          {() => (
            <Typography variant='body2' className='text-neutral-darker'>
              {intl.getHTML('SETTINGS_PAGE.TEMPLATE_PAGE.UPDATE_WARNING')}
            </Typography>
          )}
        </GlobalBanner>
      )}
      <div className='grid grid-cols-2 mt-8 gap-y-6 gap-x-10%'>
        <FormItem
          label={intl.get(
            'SETTINGS_PAGE.TEMPLATE_PAGE.OVERVIEW_SECTION.TITLE_FIELD_LABEL'
          )}
          labelProps={{ required: true }}
        >
          <TextField
            aria-label={intl.get(
              'SETTINGS_PAGE.TEMPLATE_PAGE.OVERVIEW_SECTION.TITLE_FIELD_LABEL'
            )}
            value={name}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              updateTemplateData('name', event.target.value)
            }
            readOnly={!canUpdate}
            data-cy='template-title-field'
          />
        </FormItem>
        <FormItem
          label={intl.get(
            'SETTINGS_PAGE.TEMPLATE_PAGE.OVERVIEW_SECTION.TEMPLATE_CREATOR_FIELD_LABEL'
          )}
          aria-label={intl.get(
            'SETTINGS_PAGE.TEMPLATE_PAGE.OVERVIEW_SECTION.TEMPLATE_CREATOR_FIELD_LABEL'
          )}
        >
          <TemplateCreator creator={creator} system={system || false} />
        </FormItem>
        <FormItem
          label={intl.get(
            'SETTINGS_PAGE.TEMPLATE_PAGE.OVERVIEW_SECTION.DESCRIPTION_FIELD_LABEL'
          )}
          labelProps={{ required: true }}
        >
          <TextArea
            textAreaProps={{
              'aria-label': intl.get(
                'SETTINGS_PAGE.TEMPLATE_PAGE.OVERVIEW_SECTION.DESCRIPTION_FIELD_LABEL'
              ),
              'data-cy': 'template-description-field',
            }}
            value={description || ''}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
              updateTemplateData('description', event.target.value)
            }
            readOnly={!canUpdate}
          />
        </FormItem>
        <FormItem
          label={intl.get(
            'SETTINGS_PAGE.TEMPLATE_PAGE.OVERVIEW_SECTION.TEMPLATE_OWNER_FIELD_LABEL'
          )}
          aria-label={intl.get(
            'SETTINGS_PAGE.TEMPLATE_PAGE.OVERVIEW_SECTION.TEMPLATE_OWNER_FIELD_LABEL'
          )}
        >
          <TemplateOwner
            ldUsers={ldUsers}
            owner={owner}
            owner_id={owner_id}
            system={system || false}
            onUpdateTemplateOwner={handleUpdateTemplateOwner}
          />
        </FormItem>
        {teamScopingProjectTemplates && (
          <FormItem
            label={intl.get(
              'SETTINGS_PAGE.TEMPLATE_PAGE.OVERVIEW_SECTION.ASSIGNED_TEAMS_LABEL'
            )}
          >
            {canUpdate ? (
              <MultiSelectDropdown
                options={learningTeamsForDropdown}
                values={selectedTeamValues}
                onChange={(options: FormOption[]) =>
                  onAssignedTeamsChange?.(options.map((option) => option.value))
                }
                disabled={isAssignedTeamsLoading}
                triggerProps={{
                  'data-cy': 'template-assigned-teams-field',
                }}
                multiple
              />
            ) : (
              // System templates are shared across every org and must stay
              // read-only (CGP-280). The DS MultiSelectDropdown trigger is a
              // div with role="button" tabIndex={0} that only receives
              // aria-disabled, and its keyDown handler has an unguarded
              // Tab/Escape branch that toggles the option list -- meaning a
              // `disabled` prop blocks the mouse but not the keyboard. Swap
              // in a non-interactive display, mirroring the TemplateOwner
              // pattern above.
              <div
                className='flex flex-wrap gap-2'
                data-cy='template-assigned-teams-readonly'
              >
                {selectedTeamValues.length === 0 ? (
                  <Typography variant='body' className='text-neutral-dark'>
                    -
                  </Typography>
                ) : (
                  selectedTeamValues.map((team) => (
                    <Tag key={team.value} label={team.label} readOnly />
                  ))
                )}
              </div>
            )}
          </FormItem>
        )}
      </div>
    </div>
  );
};

export default Overview;
